import { useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { RMReplyTemplate } from '@brightlocal/api-client';
import { RM_TEMPLATE_RATING_TYPE } from '@brightlocal/shared';
import { sonner } from '@brightlocal/ui-components/sonner';

import { useZodForm } from 'shared/forms';

import type { TemplateSheetTracking } from './useReplyTemplatesTracking';
import { useSaveTemplateMutation } from './useSaveTemplateMutation';
import { useTemplateFormSchema, type TemplateFormValues } from './useTemplateFormSchema';
import { TEMPLATE_FORM_FIELDS } from '../consts';
import { getTemplateErrorFields, insertAtCursor, isHandledTemplateSaveError, isTemplateGoneError } from '../utils';

const NEW_TEMPLATE_VALUES: TemplateFormValues = {
  name: '',
  content: '',
  reviewRatingType: RM_TEMPLATE_RATING_TYPE.ALL_RATINGS
};

interface Params {
  /** The template being edited; `null` for a new one. */
  template: RMReplyTemplate | null;
  /** Sends the template's save event. */
  tracking: TemplateSheetTracking;
  /** Closes the sheet. */
  onClose: () => void;
}

/** The template form: its values, the placeholder buttons, and saving with a confirmation or an error. */
export const useTemplateForm = ({ template, tracking, onClose }: Params) => {
  const { t } = useTranslation();
  const { trackTemplateCreated, trackTemplateEdited } = tracking;
  const schema = useTemplateFormSchema();
  const { mutateAsync, isPending, error } = useSaveTemplateMutation(template);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const isEdit = template !== null;

  const { form, handleFormSubmit } = useZodForm({
    schema,
    defaultValues: template
      ? { name: template.name, content: template.content, reviewRatingType: template.reviewRatingType }
      : NEW_TEMPLATE_VALUES,
    onSubmit: async (values) => {
      await mutateAsync(values);
      const trackSaved = isEdit ? trackTemplateEdited : trackTemplateCreated;

      trackSaved(values.reviewRatingType);
      sonner({
        title: t(template ? 'reports.replyTemplates.sheet.savedToast' : 'reports.replyTemplates.sheet.createdToast'),
        icon: 'success'
      });
      onClose();
    },
    onError: (submitError) => {
      if (isEdit && isTemplateGoneError(submitError)) {
        sonner({ title: t('reports.replyTemplates.sheet.goneToast'), icon: 'error' });
        onClose();

        return;
      }
      getTemplateErrorFields(submitError).forEach((field, index) => {
        const isFirstInvalidField = index === 0;

        form.setError(
          field,
          { type: 'server', message: t(`reports.replyTemplates.sheet.errors.invalid.${field}`) },
          { shouldFocus: isFirstInvalidField }
        );
      });
    }
  });

  const isSaveFailed = error !== null && !isHandledTemplateSaveError({ error, isEdit });
  const isUnchangedEdit = isEdit && !form.formState.isDirty;

  // Puts the placeholder where the cursor is, or in place of the selection, then moves the cursor after it
  const handleInsertPlaceholder = (placeholder: string) => {
    const textarea = contentRef.current;

    if (!textarea) {
      return;
    }

    const { value, cursor } = insertAtCursor({
      value: form.getValues(TEMPLATE_FORM_FIELDS.CONTENT),
      text: placeholder,
      selectionStart: textarea.selectionStart,
      selectionEnd: textarea.selectionEnd
    });

    // The new value has to be in the textarea before the cursor moves, or the cursor lands in the old text
    flushSync(() => {
      form.setValue(TEMPLATE_FORM_FIELDS.CONTENT, value, { shouldDirty: true, shouldValidate: true });
    });
    textarea.focus();
    textarea.setSelectionRange(cursor, cursor);
  };

  return {
    form,
    contentRef,
    isSaving: isPending,
    isSaveFailed,
    isUnchangedEdit,
    handleSubmit: handleFormSubmit,
    handleInsertPlaceholder
  };
};
